import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../lib/api";
import { BOOKING_DRIVER_LABEL, SOURCE_LABEL, errorMessage, humanize } from "../lib/renterDrivers";

// Task 6: self-drive driver policy and the "needs driver action" queue.
// Nothing is restrictive until the business configures it, and every rule
// names its source so a business rule is never presented as law.
export default function RenterDriverPolicy() {
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState(null);
  const [form, setForm] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(() => {
    api.get("/renter-drivers/policy").then((r) => {
      setData(r.data);
      const p = r.data.policy;
      setForm({
        min_age: p.min_age?.value ?? "", min_age_source: p.min_age?.source || "BUSINESS_POLICY",
        min_age_note: p.min_age?.evidence_note || "",
        tenure: p.min_licence_tenure_months?.value ?? "", tenure_source: p.min_licence_tenure_months?.source || "BUSINESS_POLICY",
        tenure_note: p.min_licence_tenure_months?.evidence_note || "",
        max_age_review: p.max_age_review?.value ?? "",
        foreign: p.foreign_licences?.value || "MANUAL_REVIEW",
        idp: !!p.idp_required_for_foreign?.value,
        max_additional: p.max_additional_drivers ?? "",
        fee_type: p.additional_driver_fee?.type || "FREE",
        fee_pesos: p.additional_driver_fee?.amount_centavos ? String(p.additional_driver_fee.amount_centavos / 100) : "",
        original: p.original_licence_required_at_pickup, identity: p.identity_check_required,
        all_required: p.all_listed_drivers_must_be_eligible, before_confirm: p.require_driver_eligibility_before_confirmation,
        booking_mode: p.booking_mode,
      });
    }).catch((err) => { if (err?.response?.status === 403) setDenied(true); else toast.error(errorMessage(err)); });
    api.get("/renter-drivers/queue").then((r) => setQueue(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (denied) return <div className="rounded-xl border bg-white p-6" data-testid="rdp-denied">You don&apos;t have access to self-drive driver settings.</div>;
  if (!data || !form) return <div className="text-slate-500">Loading…</div>;
  const canEdit = (data.capabilities || []).includes("renter_drivers.policy_configure");

  const rule = (value, source, note) => (value === "" ? null : { value: Number(value), source, evidence_note: note || null });
  const save = async () => {
    const cents = form.fee_pesos === "" ? null : Math.round(Number(form.fee_pesos) * 100);
    try {
      await api.put("/renter-drivers/policy", {
        min_age: rule(form.min_age, form.min_age_source, form.min_age_note),
        min_licence_tenure_months: rule(form.tenure, form.tenure_source, form.tenure_note),
        max_age_review: rule(form.max_age_review, "BUSINESS_POLICY"),
        foreign_licences: { value: form.foreign, source: "BUSINESS_POLICY" },
        idp_required_for_foreign: form.idp ? { value: true, source: "BUSINESS_POLICY" } : null,
        max_additional_drivers: form.max_additional === "" ? null : Number(form.max_additional),
        additional_driver_fee: { type: form.fee_type, amount_centavos: ["FIXED_PER_DRIVER", "PER_DAY"].includes(form.fee_type) ? cents : null },
        original_licence_required_at_pickup: form.original, identity_check_required: form.identity,
        all_listed_drivers_must_be_eligible: form.all_required,
        require_driver_eligibility_before_confirmation: form.before_confirm, booking_mode: form.booking_mode,
      });
      toast.success("Policy saved. Future bookings are re-checked; confirmed bookings are flagged, not rewritten.");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const input = "mt-1 w-full min-w-0 px-3 py-2 rounded-lg border border-slate-300 text-sm";
  const sourceSelect = (key) => (
    <select className={input} value={form[key]} onChange={set(key)} aria-label="Rule source">
      {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="renter-driver-policy">
      <div>
        <h1 className="font-display text-2xl font-bold">Self-drive drivers</h1>
        <p className="text-sm text-slate-600">{data.note}</p>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="rdp-queue">
        <h2 className="font-semibold">Upcoming bookings needing driver action</h2>
        {queue?.items?.length ? (
          <ul className="divide-y divide-slate-100 text-sm">
            {queue.items.map((b) => (
              <li key={b.id} className="py-2 flex flex-wrap justify-between gap-2">
                <Link to={`/app/bookings/${b.id}`} className="underline">{b.booking_reference} · {b.vehicle_name}</Link>
                <span>{BOOKING_DRIVER_LABEL[b.self_drive_driver_status] || "Driver information required"}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-slate-500">No upcoming self-drive booking needs driver action.</p>}
      </section>

      <fieldset disabled={!canEdit} className="rounded-xl border border-slate-200 bg-white p-4 grid sm:grid-cols-2 gap-3">
        <legend className="px-1 font-semibold">Driver policy</legend>
        <label className="text-sm"><span className="text-xs text-slate-600">Minimum driver age (leave empty if none)</span>
          <input type="number" min="16" className={input} value={form.min_age} onChange={set("min_age")} /></label>
        <label className="text-sm"><span className="text-xs text-slate-600">Where this age rule comes from</span>{sourceSelect("min_age_source")}</label>
        {form.min_age_source !== "BUSINESS_POLICY" && (
          <label className="text-sm sm:col-span-2"><span className="text-xs text-slate-600">Evidence (policy clause, endorsement, rule)</span>
            <input className={input} value={form.min_age_note} onChange={set("min_age_note")} /></label>
        )}
        <label className="text-sm"><span className="text-xs text-slate-600">Minimum months licensed</span>
          <input type="number" min="0" className={input} value={form.tenure} onChange={set("tenure")} /></label>
        <label className="text-sm"><span className="text-xs text-slate-600">Where this rule comes from</span>{sourceSelect("tenure_source")}</label>
        {form.tenure_source !== "BUSINESS_POLICY" && (
          <label className="text-sm sm:col-span-2"><span className="text-xs text-slate-600">Evidence</span>
            <input className={input} value={form.tenure_note} onChange={set("tenure_note")} /></label>
        )}
        <label className="text-sm"><span className="text-xs text-slate-600">Review drivers aged at least</span>
          <input type="number" min="16" className={input} value={form.max_age_review} onChange={set("max_age_review")} /></label>
        <label className="text-sm"><span className="text-xs text-slate-600">Foreign licences</span>
          <select className={input} value={form.foreign} onChange={set("foreign")}>
            <option value="ACCEPT">Accept eligible foreign licences</option>
            <option value="MANUAL_REVIEW">Review each one</option>
            <option value="NOT_ACCEPTED">Not accepted</option>
          </select></label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.idp} onChange={set("idp")} /> Require an IDP with foreign licences (business policy)</label>
        <label className="text-sm"><span className="text-xs text-slate-600">Additional drivers allowed (empty = no limit)</span>
          <input type="number" min="0" className={input} value={form.max_additional} onChange={set("max_additional")} /></label>
        <label className="text-sm"><span className="text-xs text-slate-600">Additional driver fee</span>
          <select className={input} value={form.fee_type} onChange={set("fee_type")}>
            {["FREE", "FIXED_PER_DRIVER", "PER_DAY", "CUSTOM_QUOTE"].map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
          </select></label>
        {["FIXED_PER_DRIVER", "PER_DAY"].includes(form.fee_type) && (
          <label className="text-sm"><span className="text-xs text-slate-600">Fee amount (₱)</span>
            <input inputMode="decimal" className={input} value={form.fee_pesos} onChange={set("fee_pesos")} /></label>
        )}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.original} onChange={set("original")} /> Inspect the original licence at pickup</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.identity} onChange={set("identity")} /> Check each driver&apos;s identity at pickup</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.all_required} onChange={set("all_required")} /> Every listed driver must be eligible before release</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.before_confirm} onChange={set("before_confirm")} /> Drivers must be eligible before the customer can confirm</label>
        <label className="text-sm sm:col-span-2"><span className="text-xs text-slate-600">Online requests</span>
          <select className={input} value={form.booking_mode} onChange={set("booking_mode")}>
            <option value="REQUEST_PENDING_DRIVER_REVIEW">Allow requests; driver reviewed before pickup</option>
            <option value="ELIGIBILITY_REQUIRED_BEFORE_REQUEST">Licence details required with the request</option>
          </select></label>
      </fieldset>
      {canEdit && <button onClick={save} className="px-4 py-2 rounded-lg btn-brand text-sm" data-testid="rdp-save">Save policy</button>}

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="font-semibold text-sm">What customers see</h2>
        <ul className="text-sm mt-1 space-y-0.5">
          {data.disclosure_preview.map((r) => <li key={r.key}>{r.text} <span className="text-xs text-slate-500">— {SOURCE_LABEL[r.source]}</span></li>)}
        </ul>
      </section>
    </div>
  );
}
